/**
 * Theme control.
 *
 * The Schoolasium DS ships dark as the default theme and light as opt-in, so
 * that is what we mirror. The initial value is applied by an inline script in
 * index.html before first paint; this hook only keeps React in sync with the
 * attribute that script already set, which is why it reads from the DOM rather
 * than owning the initial state itself.
 */

import { useCallback, useEffect, useState } from "react";

export type Theme = "dark" | "light";

export const THEME_STORAGE_KEY = "esp-flasher-theme";

function currentTheme(): Theme {
  if (typeof document === "undefined") return "dark";
  return document.documentElement.getAttribute("data-theme") === "light" ? "light" : "dark";
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(currentTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Private browsing rejects writes; the theme just will not persist.
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((current) => (current === "dark" ? "light" : "dark"));
  }, []);

  return { theme, toggleTheme };
}
